import React, { useEffect, useMemo, useRef, useState } from 'react';
import LoadingContent from 'lqa-ui/lib/Components/LoadingContent';
import type { TrackEventHandler } from '@lqa-frontend/shared';
import { getItem } from 'helpers/storage';
import { useCustomerBrandHotelSelection } from 'components/customerBrandHotelSelect/useCustomerBrandHotelSelection';
import { Analytics } from '../common/utils/analytics';

interface IRemoteWorkspaceSelection {
    id: number;
    name: string;
}

interface IRemoteMountOptions {
    basename?: string;
    token?: string | null;
    trackEvent?: TrackEventHandler;
    brand?: IRemoteWorkspaceSelection;
    hotel?: IRemoteWorkspaceSelection;
}

// A remote may be the bundle itself (synchronous mount) or a loader facade that
// resolves the real bundle lazily, so both hooks can return a promise.
interface IRemoteApi {
    mount: (element: HTMLElement, options?: IRemoteMountOptions) => void | Promise<void>;
    unmount: (element: HTMLElement) => void | Promise<void>;
}

// Embedded remotes share this window, so there is no iframe `postMessage`
// bridge (see MicroApp) to forward their Mixpanel events. Hand them a direct
// callback instead — synchronous, so events sent during page unload still go
// out, over `sendBeacon`.
const handleRemoteTrackEvent: TrackEventHandler = (eventName, properties, options) => {
    Analytics.sendEvent(eventName, properties, options?.isPageUnloading ? { transport: 'sendBeacon' } : undefined);
};

interface IMicroAppEmbedProps {
    bundleUrl: string;
    globalName: string;
    basename: string;
}

const loadPromises = new Map<string, Promise<IRemoteApi>>();

function isEsmDevEntry(bundleUrl: string): boolean {
    return /\.tsx?(?:[?#].*)?$/i.test(bundleUrl);
}

function isLocalHostUrl(bundleUrl: string): boolean {
    try {
        const { hostname } = new URL(bundleUrl);
        return hostname === 'localhost' || hostname === '127.0.0.1';
    } catch {
        return /localhost|127\.0\.0\.1/i.test(bundleUrl);
    }
}

function toLocalHmrEntryUrl(bundleUrl: string): string {
    return new URL('src/embed-entry.tsx', bundleUrl).href;
}

async function loadEsmEntry(entryUrl: string): Promise<IRemoteApi> {
    const mod = await import(/* @vite-ignore */ entryUrl);
    if (typeof mod.mount !== 'function' || typeof mod.unmount !== 'function') {
        throw new TypeError(`"${entryUrl}" must export mount and unmount`);
    }
    return { mount: mod.mount, unmount: mod.unmount };
}

async function loadIifeScript(bundleUrl: string, globalName: string, cacheKey: string): Promise<IRemoteApi> {
    const win = window as unknown as Window & Record<string, IRemoteApi | undefined>;
    const script = document.createElement('script');
    script.src = bundleUrl;
    script.async = true;

    await new Promise<void>((resolve, reject) => {
        script.onload = () => resolve();
        script.onerror = () => {
            script.remove();
            loadPromises.delete(cacheKey);
            reject(new Error(`Failed to load embed bundle from ${bundleUrl}`));
        };
        document.head.appendChild(script);
    });

    const api = win[globalName];
    if (!api) {
        script.remove();
        loadPromises.delete(cacheKey);
        throw new Error(`"${globalName}" global not found after loading ${bundleUrl}`);
    }
    return api;
}

async function loadRemote(bundleUrl: string, globalName: string): Promise<IRemoteApi> {
    const win = window as unknown as Window & Record<string, IRemoteApi | undefined>;
    if (win[globalName]) {
        return win[globalName];
    }

    const cacheKey = `${globalName}:${bundleUrl}`;
    const existing = loadPromises.get(cacheKey);
    if (existing) {
        return existing;
    }

    const promise = (async () => {
        if (isEsmDevEntry(bundleUrl)) {
            return loadEsmEntry(bundleUrl);
        }

        // Local: try IIFE (yarn preview:embed), then HMR entry (yarn dev).
        if (isLocalHostUrl(bundleUrl)) {
            try {
                return await loadIifeScript(bundleUrl, globalName, cacheKey);
            } catch {
                return loadEsmEntry(toLocalHmrEntryUrl(bundleUrl));
            }
        }

        return loadIifeScript(bundleUrl, globalName, cacheKey);
    })();

    loadPromises.set(cacheKey, promise);
    return promise;
}

// Clears both the in-flight cache and the window global so a later remount can
// reload the loader. Needed when a loader facade was installed but its hashed
// bundle then failed — otherwise loadRemote would keep returning that dead API.
function clearRemoteCache(bundleUrl: string, globalName: string): void {
    loadPromises.delete(`${globalName}:${bundleUrl}`);
    const win = window as unknown as Window & Record<string, IRemoteApi | undefined>;
    delete win[globalName];
}

function toWorkspaceSelection(
    id: number | undefined,
    name: string | undefined
): IRemoteWorkspaceSelection | undefined {
    if (id == null || !name) {
        return undefined;
    }
    return { id, name };
}

export function MicroAppEmbed({ bundleUrl, globalName, basename }: Readonly<IMicroAppEmbedProps>) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const { selectedBrand, selectedHotel, selectedBrandId, selectedHotelId } =
        useCustomerBrandHotelSelection();

    const brand = useMemo(
        () => toWorkspaceSelection(selectedBrandId, selectedBrand?.name),
        [selectedBrandId, selectedBrand?.name]
    );
    const hotel = useMemo(
        () => toWorkspaceSelection(selectedHotelId, selectedHotel?.name),
        [selectedHotelId, selectedHotel?.name]
    );

    useEffect(() => {
        let isActive = true;
        const element = containerRef.current;
        let api: IRemoteApi | undefined;

        setIsLoading(true);
        setError(null);

        loadRemote(bundleUrl, globalName)
            .then(async (remote) => {
                if (!isActive || !element) {
                    return;
                }
                api = remote;
                const userData = getItem('user');
                // Awaited so a mount that resolves its bundle lazily keeps the
                // loading state until content is rendered, and surfaces a
                // failed load through the catch below instead of a blank frame.
                await remote.mount(element, {
                    basename,
                    token: userData?.token ?? null,
                    trackEvent: handleRemoteTrackEvent,
                    brand,
                    hotel,
                });
                if (isActive) {
                    setIsLoading(false);
                }
            })
            .catch((loadError: Error) => {
                // Always clear, even if unmounted — otherwise the next visit
                // reuses a rejected loader facade and cannot recover.
                clearRemoteCache(bundleUrl, globalName);
                if (isActive) {
                    setError(loadError.message);
                    setIsLoading(false);
                }
            });

        return () => {
            isActive = false;
            if (api && element) {
                const unmountResult = api.unmount(element);
                if (unmountResult instanceof Promise) {
                    // Bundle never loaded or remount raced unmount — nothing to clean up.
                    unmountResult.catch(() => {});
                }
            }
        };
    }, [basename, brand, bundleUrl, globalName, hotel]);

    if (error) {
        return <div style={{ padding: 24, color: '#b00020' }}>Failed to load microfrontend. {error}</div>;
    }

    return (
        <div>
            {isLoading && <LoadingContent layout="absolute" />}
            <div ref={containerRef} />
        </div>
    );
}
